import { z } from 'zod';

export const loginFormSchema = z.object({
  email: z.string({ message: 'E-mail jest wymagany' }).email({ message: 'Błędny adres e-mail' }),
  password: z.string({ message: 'Hasło jest wymagane' }),
});

export const userSchema = z.object({
  id: z.number(),
  email: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  branch: z.string().nullable(),
  phone: z.string().nullable(),
  account_type: z.string().optional(),
});

export const educationalMaterialSchema = z.object({
  content: z.string(),
  title: z.string().nullable(),
  type: z.union([
    z.literal('textarea'),
    z.literal('file'),
    z.literal('image'),
    z.literal('url'),
    z.literal('youtube'),
  ]),
  id: z.number(),
});

export const productOption = z.object({
  name: z.string(),
  value: z.string().nullable(),
  id: z.number(),
  slug: z.string(),
  type: z.union([
    z.literal('string'),
    z.literal('boolean'),
    z.literal('float'),
    z.literal('integer'),
  ]),
});

export const priceSchema = z.object({
  id: z.number(),
  price: z.string(),
  price_float: z.number(),
  valid_to: z.string().nullable(),
  valid_from: z.string(),
});

export const pricesSchema = z.array(priceSchema);

export const productSchema = z.object({
  id: z.number(),
  name: z.string(),
  is_available: z.boolean(),
  is_archived: z.boolean(),
  is_group: z.boolean(),
  educational_materials: z.array(educationalMaterialSchema),
  product_price: z.object({
    id: z.number(),
  }),
  product_prices: z.array(priceSchema),
  product_options: z.array(productOption),
  product_groups: z.array(
    z.object({
      id: z.number(),
      slug: z.string(),
      product: z.object({
        name: z.string(),
        product_options: z.array(productOption),
        product_price: z.object({
          price_float: z.number(),
        }),
        value: z.union([z.string(), z.number()]).nullable(),
      }),
      quantity: z.number(),
    })
  ),
  value: z.string().nullable(),
  zum_name: z.string().nullable(),
});

export const simpleProductSchema = z.object({
  id: z.number(),
  name: z.string(),
  product_options: z.array(productOption),
  product_groups: z.array(
    z.object({
      id: z.number(),
      slug: z.string(),
      product: z.object({
        name: z.string(),
        product_options: z.array(productOption),
        product_price: z.object({
          price_float: z.number(),
        }),
        value: z.union([z.string(), z.number()]).nullable(),
      }),
      quantity: z.number(),
    })
  ),
  value: z.string().nullable(),
  category_id: z.number().nullable(),
  product_price: z.object({
    price_float: z.number(),
    id: z.number(),
  }),
  product_prices: z.array(priceSchema),
  zum_name: z.string().nullable(),
});

export const linkSchema = z.object({
  id: z.number(),
  title: z.string(),
  description: z.string(),
  url: z.string(),
  type: z.string(),
  icon: z.string(),
  order: z.number(),
  open_in_new_tab: z.boolean(),
});

export const simpleProductsSchema = z.array(simpleProductSchema);

export const productItemSchema = z.object({
  id: z.number(),
  name: z.string(),
  type: z.literal('product'),
  view_id: z.number(),
  products: z.array(productSchema),
  is_hidden: z.boolean(),
  tip: z.string().nullable(),
  links: z.array(linkSchema)
});

export const valueItemSchema = z.object({
  id: z.number(),
  name: z.string(),
  type: z.literal('value'),
  value: z.string().nullable(),
  value_type: z.union([
    z.literal('string'),
    z.literal('boolean'),
    z.literal('float'),
    z.literal('integer'),
  ]),
  value_unit: z.string().nullable(),
  view_id: z.number(),
  is_hidden: z.boolean(),
  tip: z.string().nullable(),
  links: z.array(linkSchema),
});

export const ruleItemSchema = z.object({
  id: z.number(),
  name: z.string(),
  type: z.literal('rule'),
  rule_id: z.number(),
  view_id: z.number(),
  is_hidden: z.boolean(),
  tip: z.string().nullable(),
  order: z.number(),
  is_required: z.boolean(),
  category_id: z.number().nullable(),
  slug: z.string(),
  pdf_field_identifier: z.string().nullable(),
  links: z.array(linkSchema),
  created_at: z.string(),
  updated_at: z.string(),
});
export const itemSchema = z.union([productItemSchema, valueItemSchema, ruleItemSchema]);

export const simpleItemsSchema = z.array(
  z.union([
    valueItemSchema,
    z.object({
      id: z.number(),
      name: z.string(),
      type: z.literal('product'),
      view_id: z.number(),
      is_archived: z.boolean(),
      is_available: z.boolean(),
    }),
  ])
);

export const viewSchema = z.object({
  id: z.number(),
  name: z.string(),
  view_group_id: z.number(),
  items: z.array(itemSchema),
  links: z.array(linkSchema)
});

export const operatorSchema = z.object({
  type: z.literal('operator'),
  key: z.union([
    z.literal('addition'),
    z.literal('subtraction'),
    z.literal('multiplication'),
    z.literal('division'),
    z.literal('equal'),
    z.literal('not_equal'),
    z.literal('gt'),
    z.literal('gte'),
    z.literal('lt'),
    z.literal('lte'),
    z.literal('right_parenthesis'),
    z.literal('left_parenthesis'),
    z.literal('and'),
    z.literal('or'),
  ]),
  data: z.object({
    display_name: z.string(),
  }),
});

export const substringParamsSchema = z.object({
  rule_id: z.union([z.number(), z.string()]),
  offset: z.union([z.number(), z.string()]),
  length: z.union([z.number(), z.string()]),
});

export const substringFunctionSchema = z.object({
  key: z.literal('substring'),
  data: z.object({
    params: substringParamsSchema,
  }),
});

export const minFunctionParamsSchema = z.object({
  rule_ids: z.array(z.union([z.number(), z.string()])),
});

export const minFunctionSchema = z.object({
  key: z.literal('min'),
  data: z.object({
    params: minFunctionParamsSchema,
  }),
});

export const joinFunctionParamsSchema = z.object({
  rule_ids: z.array(z.union([z.number(), z.string()])),
});

export const joinFunctionSchema = z.object({
  key: z.literal('join'),
  data: z.object({
    params: joinFunctionParamsSchema,
  }),
});

export const maxFunctionParamsSchema = z.object({
  rule_ids: z.array(z.union([z.number(), z.string()])),
});

export const maxFunctionSchema = z.object({
  key: z.literal('max'),
  data: z.object({
    params: maxFunctionParamsSchema,
  }),
});

export const valueFunctionParamsSchema = z.union([
  z.object({
    string: z.string(),
    type: z.union([z.literal('boolean'), z.literal('float'), z.literal('integer')]),
  }),
  z.object({
    string: z.string().nullable(),
    type: z.literal('string'),
  }),
]);

export const valueFunctionSchema = z.object({
  key: z.literal('value'),
  data: z.object({
    params: valueFunctionParamsSchema,
  }),
});

export const roundFunctionParamsSchema = z.object({
  rule_id: z.union([z.number(), z.string()]),
  precision: z.union([z.number(), z.string()]),
  type: z.union([z.literal('Default'), z.literal('Up'), z.literal('Down')]),
});

export const roundFunctionSchema = z.object({
  key: z.literal('round'),
  data: z.object({
    params: roundFunctionParamsSchema,
  }),
});

export const replaceFunctionParamsSchema = z.object({
  search_rule_id: z.union([z.number(), z.string()]),
  replace_rule_id: z.union([z.number(), z.string()]),
  subject_rule_id: z.union([z.number(), z.string()]),
});

export const replaceFunctionSchema = z.object({
  key: z.literal('replace'),
  data: z.object({
    params: replaceFunctionParamsSchema,
  }),
});

export const pmtFunctionParamsSchema = z.object({
  rate_rule_id: z.union([z.number(), z.string()]),
  periods_rule_id: z.union([z.number(), z.string()]),
  principal_rule_id: z.union([z.number(), z.string()]),
  future_value_rule_id: z.union([z.number(), z.string()]),
});

export const pmtFunctionSchema = z.object({
  key: z.literal('pmt'),
  data: z.object({
    params: pmtFunctionParamsSchema,
  }),
});

export const functionSchema = z
  .object({
    type: z.literal('function'),
  })
  .and(
    z.union([
      substringFunctionSchema,
      minFunctionSchema,
      maxFunctionSchema,
      valueFunctionSchema,
      roundFunctionSchema,
      joinFunctionSchema,
      replaceFunctionSchema,
      pmtFunctionSchema,
    ])
  );

export const switchParamsSchema = z.object({
  expression: z.union([z.number(), z.string()]),
  key_value_rule_ids: z.array(
    z.object({
      key: z.union([z.number(), z.string()]),
      value: z.union([z.number(), z.string()]),
    })
  ),
  default_rule_id: z.union([z.number(), z.string()]),
});

export const switchSchema = z.object({
  type: z.literal('instruction'),
  key: z.literal('switch'),
  data: z.object({
    params: switchParamsSchema,
  }),
});

export const ifParamsSchema = z.object({
  condition: z.union([z.number(), z.string()]),
  true_statement: z.union([z.number(), z.string()]),
  false_statement: z.union([z.number(), z.string()]),
});

export const instructionSchema = z.object({
  type: z.literal('instruction'),
  key: z.literal('if'),
  data: z.object({
    params: ifParamsSchema,
  }),
});

export const productOptionValueByItemAndProductOptionSlugParamsSchema = z.object({
  item_id: z.union([z.number(), z.string()]),
  product_option_slug: z.string(),
});

export const productOptionValueByItemAndProductOptionSlugSchema = z.object({
  key: z.literal('product_option_value_by_item_and_product_option_slug'),
  data: z.object({
    params: productOptionValueByItemAndProductOptionSlugParamsSchema,
  }),
});

export const productOptionValueByProductIdAndProductOptionSlugParamsSchema = z.object({
  product_id: z.union([z.number(), z.string()]),
  product_option_slug: z.string(),
});

export const productOptionValueByProductIdAndProductOptionSlugSchema = z.object({
  key: z.literal('product_option_value_by_product_id_and_product_option_slug'),
  data: z.object({
    params: productOptionValueByProductIdAndProductOptionSlugParamsSchema,
  }),
});

export const ruleByIdParamsSchema = z.object({
  rule_id: z.union([z.number(), z.string()]),
});

export const ruleByIdSchema = z.object({
  key: z.literal('rule_by_id'),
  data: z.object({
    params: ruleByIdParamsSchema,
  }),
});

export const selectionValueByItemParamsSchema = z.object({
  item_id: z.union([z.number(), z.string()]),
});

export const selectionValueByItemSchema = z.object({
  key: z.literal('selection_value_by_item'),
  data: z.object({
    params: selectionValueByItemParamsSchema,
  }),
});

export const productPriceByItemParamsSchema = z.object({
  item_id: z.union([z.number(), z.string()]),
});

export const productPriceByItemSchema = z.object({
  key: z.literal('product_price_by_item'),
  data: z.object({
    params: productPriceByItemParamsSchema,
  }),
});

export const productPriceByProductIdParamsSchema = z.object({
  product_id: z.union([z.number(), z.string()]),
});

export const productPriceByProductIdSchema = z.object({
  key: z.literal('product_price_by_product_id'),
  data: z.object({
    params: productPriceByProductIdParamsSchema,
  }),
});

export const productNameByIdSchema = z.object({
  key: z.literal('product_name_by_id'),
  data: z.object({
    params: productPriceByProductIdParamsSchema,
  }),
});

export const productZumNameByIdSchema = z.object({
  key: z.literal('product_zum_name_by_id'),
  data: z.object({
    params: productPriceByProductIdParamsSchema,
  }),
});

export const productPriceByIdParamsSchema = z.object({
  price_id: z.union([z.number(), z.string()]),
});

export const productPriceByIdSchema = z.object({
  key: z.literal('product_price_by_id'),
  data: z.object({
    params: productPriceByIdParamsSchema,
  }),
});

export const assignedProductOptionValueByItemAndAssignedProductSlugAndProductOptionSlugParamsSchema =
  z.object({
    item_id: z.union([z.number(), z.string()]),
    assigned_product_slug: z.string(),
    product_option_slug: z.string(),
  });

export const assignedProductOptionValueByItemAndAssignedProductSlugAndProductOptionSlugSchema =
  z.object({
    key: z.literal(
      'assigned_product_option_value_by_item_and_assigned_product_slug_and_product_option_slug'
    ),
    data: z.object({
      params:
        assignedProductOptionValueByItemAndAssignedProductSlugAndProductOptionSlugParamsSchema,
    }),
  });

export const productValueByItemParamsSchema = z.object({
  item_id: z.union([z.number(), z.string()]),
});

export const productValueByItemSchema = z.object({
  key: z.literal('product_value_by_item'),
  data: z.object({
    params: productValueByItemParamsSchema,
  }),
});

export const grantLevelOptionValueByGrantAndGrantLevelItemAndGrantLevelOptionSlugParamsSchema =
  z.object({
    grant_id: z.union([z.number(), z.string()]),
    item_id: z.union([z.number(), z.string()]),
    grant_level_option_slug: z.string(),
  });

export const grantLevelOptionValueByGrantAndGrantLevelItemAndGrantLevelOptionSlugSchema = z.object({
  key: z.literal(
    'grant_level_option_value_by_grant_and_grant_level_item_and_grant_level_option_slug'
  ),
  data: z.object({
    params: grantLevelOptionValueByGrantAndGrantLevelItemAndGrantLevelOptionSlugParamsSchema,
  }),
});

export const grantLevelOptionValueByIdSchemaParamsSchema = z.object({
  grant_level_option_id: z.union([z.number(), z.string()]),
});

export const grantLevelOptionValueByIdSchema = z.object({
  key: z.literal('grant_level_option_value_by_id'),
  data: z.object({
    params: grantLevelOptionValueByIdSchemaParamsSchema,
  }),
});

export const assignedProductQuantityByItemAndAssignedProductSlugParamsSchema = z.object({
  item_id: z.union([z.number(), z.string()]),
  assigned_product_slug: z.string(),
});

export const assignedProductQuantityByItemAndAssignedProductSlugSchema = z.object({
  key: z.literal('assigned_product_quantity_by_item_and_assigned_product_slug'),
  data: z.object({
    params: assignedProductQuantityByItemAndAssignedProductSlugParamsSchema,
  }),
});

export const assignedProductPriceByItemAndAssignedProductSlugParamsSchema = z.object({
  item_id: z.union([z.number(), z.string()]),
  assigned_product_slug: z.string(),
});

export const assignedProductPriceByItemAndAssignedProductSlugSchema = z.object({
  key: z.literal('assigned_product_price_by_item_and_assigned_product_slug'),
  data: z.object({
    params: assignedProductPriceByItemAndAssignedProductSlugParamsSchema,
  }),
});

export const assignedProductNameByItemAndAssignedProductSlugParamsSchema = z.object({
  item_id: z.union([z.number(), z.string()]),
  assigned_product_slug: z.string(),
});

export const assignedProductNameByItemAndAssignedProductSlugSchema = z.object({
  key: z.literal('assigned_product_name_by_item_and_assigned_product_slug'),
  data: z.object({
    params: assignedProductNameByItemAndAssignedProductSlugParamsSchema,
  }),
});

export const isSegmentAddedToCalculationParamsSchema = z.object({
  segment_id: z.union([z.number(), z.string()]),
});

export const isSegmentAddedToCalculationSchema = z.object({
  key: z.literal('is_segment_added_to_calculation'),
  data: z.object({
    params: isSegmentAddedToCalculationParamsSchema,
  }),
});

export const calculationDateParamsSchema = z.object({
  operator: z.union([z.literal('none'), z.literal('add'), z.literal('subtract')]),
  rule_id: z.union([z.number(), z.string()]),
});

export const calculationDateSchema = z.object({
  key: z.literal('calculation_date'),
  data: z.object({
    params: calculationDateParamsSchema,
  }),
});

export const assignedProductValueByItemAndAssignedProductSlugParamsSchema = z.object({
  item_id: z.union([z.number(), z.string()]),
  assigned_product_slug: z.string(),
});

export const assignedProductValueByItemAndAssignedProductSlugSchema = z.object({
  key: z.literal('assigned_product_value_by_item_and_assigned_product_slug'),
  data: z.object({
    params: assignedProductValueByItemAndAssignedProductSlugParamsSchema,
  }),
});

export const productNameByItemSchemaParamsSchema = z.object({
  item_id: z.union([z.number(), z.string()]),
});

export const productZumNameByItemSchema = z.object({
  key: z.literal('product_zum_name_by_item'),
  data: z.object({
    params: productNameByItemSchemaParamsSchema,
  }),
});

export const productNameByItemSchema = z.object({
  key: z.literal('product_name_by_item'),
  data: z.object({
    params: productNameByItemSchemaParamsSchema,
  }),
});

export const productValueByIdParamsSchema = z.object({
  product_id: z.union([z.number(), z.string()]),
});

export const productValueByIdSchema = z.object({
  key: z.literal('product_value_by_id'),
  data: z.object({
    params: productValueByIdParamsSchema,
  }),
});

export const productPriceByCategoryAndProductOptionSlugAndProductOptionValueParamsSchema = z.object(
  {
    category_id: z.union([z.number(), z.string()]),
    product_option_slug: z.string(),
    rule_id: z.union([z.number(), z.string()]),
  }
);

export const productPriceByCategoryAndProductOptionSlugAndProductOptionValueSchema = z.object({
  key: z.literal('product_price_by_category_and_product_option_slug_and_product_option_value'),
  data: z.object({
    params: productPriceByCategoryAndProductOptionSlugAndProductOptionValueParamsSchema,
  }),
});

export const tagSchema = z
  .object({
    type: z.literal('tag'),
  })
  .and(
    z.union([
      assignedProductNameByItemAndAssignedProductSlugSchema,
      assignedProductOptionValueByItemAndAssignedProductSlugAndProductOptionSlugSchema,
      assignedProductPriceByItemAndAssignedProductSlugSchema,
      assignedProductQuantityByItemAndAssignedProductSlugSchema,
      assignedProductValueByItemAndAssignedProductSlugSchema,
      calculationDateSchema,
      grantLevelOptionValueByGrantAndGrantLevelItemAndGrantLevelOptionSlugSchema,
      grantLevelOptionValueByIdSchema,
      isSegmentAddedToCalculationSchema,
      productNameByItemSchema,
      productOptionValueByItemAndProductOptionSlugSchema,
      productPriceByIdSchema,
      productPriceByItemSchema,
      productValueByIdSchema,
      productValueByItemSchema,
      ruleByIdSchema,
      selectionValueByItemSchema,
      productPriceByCategoryAndProductOptionSlugAndProductOptionValueSchema,
      productZumNameByItemSchema,
      productPriceByProductIdSchema,
      productNameByIdSchema,
      productZumNameByIdSchema,
      productOptionValueByProductIdAndProductOptionSlugSchema,
    ])
  );

export const ruleSchema = z.union([
  tagSchema,
  instructionSchema,
  functionSchema,
  operatorSchema,
  switchSchema,
]);

export const rulesSchema = z.array(ruleSchema);

export const ruleType = z.union([
  z.literal('calculation'),
  z.literal('presentation'),
  z.literal('contract'),
  z.literal('tile'),
]);

export const viewGroupSchema = z.object({
  id: z.number(),
  name: z.string(),
  segment_id: z.number(),
  views: z.array(viewSchema),
  is_skipable: z.boolean(),
  rules: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      is_visible: z.union([z.boolean(), z.number()]),
      is_hidden: z.union([z.boolean(), z.number()]),
      parent_id: z.number().nullable(),
      rules: rulesSchema,
      unit: z.string().nullable(),
      color: z.string().nullable(),
      type: ruleType,
    })
  ),
});

export const allRuleItemSchema = z.object({
  id: z.number(),
  rules: z.array(ruleSchema),
  is_visible: z.union([z.boolean(), z.number()]),
  parent_id: z.number().nullable(),
  type: ruleType,
  slug: z.string(),
  unit: z.string().nullable(),
  pdf_field_identifier: z.string().nullable(),
  ruleable_id: z.number().nullable(),
  ruleable_type: z.string().nullable(),
  name: z.string(),
});

export const allRulesSchema = z.array(allRuleItemSchema);

export const segmentSchema = z.object({
  id: z.number(),
  name: z.string(),
  calculator_id: z.number(),
  view_groups: z.array(viewGroupSchema),
  is_required: z.number(),
  rules: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      is_visible: z.union([z.boolean(), z.number()]),
      parent_id: z.number().nullable(),
      rules: rulesSchema,
      unit: z.string().nullable(),
      color: z.string().nullable(),
      type: ruleType,
    })
  ),
  links: z.array(linkSchema),
});

export const calculatorSchema = z.object({
  id: z.number(),
  name: z.string(),
  version: z.string(),
  segments: z.array(segmentSchema),
  calculation_rules: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      is_visible: z.union([z.boolean(), z.number()]),
      parent_id: z.number().nullable(),
      rules: rulesSchema,
      unit: z.string().nullable(),
      color: z.string().nullable(),
      pdf_field_identifier: z.string().nullable(),
    })
  ),
});

export const calculatorsSchema = z.array(calculatorSchema);

export const clientPropsSchema = z.object({
  first_name: z.string({ message: 'Podanie imienia jest wymagane.' }),
  last_name: z.string({ message: 'Podanie nazwiska jest wymagane.' }),
  email: z
    .string({ message: 'Podanie adresu e-mail jest wymagane.' })
    .email({ message: 'Adres e-mail jest niepoprawny.' }),
  phone: z
    .string({ message: 'Podanie numeru telefonu jest wymagane.' })
    .refine((val) => !val || /^\d{9}$/.test(val), {
      message: 'Numer telefonu jest niepoprawny.',
    }),
  street: z.string({ message: 'Podanie adresu jest wymagane.' }),
  house_number: z.string({ message: 'Podanie numeru domu jest wymagane.' }),
  apartment_number: z.string().nullable().optional(),
  province: z.string({ message: 'Wybór województwa jest wymagany.' }),
  city: z.string({ message: 'Podanie miasta jest wymagane.' }),
  postal_code: z
    .string({ message: 'Podanie kodu pocztowego jest wymagane.' })
    .regex(/^\d{2}-\d{3}$/, { message: 'Kod pocztowy jest w niepoprawnym formacie.' }),
});

export const clientSchema = z
  .object({
    id: z.number().nullable(),
    owner_user_id: z.number(),
    uuid: z.string(),
    outdated: z.boolean().optional(),
    owner: z
      .object({
        id: z.number(),
        email: z.string(),
        first_name: z.string(),
        last_name: z.string(),
        branch: z.string().nullable(),
        phone: z.string().nullable(),
        account_type: z.string(),
        created_at: z.string(),
        updated_at: z.string(),
      })
      .optional(),
  })
  .merge(clientPropsSchema);

export const clientsSchema = z.array(clientSchema);

export const calculationSegmentValueItem = z.object({
  value: z.union([z.string(), z.boolean(), z.number()]).nullable(),
  uuid: z.string(),
  type: z.literal('value'),
  item_id: z.number(),
  calculation_segment_uuid: z.string(),
  calculation_segment_id: z.number().nullable(),
});

export const calculationSegmentProductItem = z.object({
  uuid: z.string(),
  type: z.literal('product'),
  product_id: z.number(),
  product_price_id: z.number(),
  item_id: z.number(),
  calculation_segment_uuid: z.string(),
  calculation_segment_id: z.number().nullable(),
});

export const calculationSegmentRuleItem = z.object({
  uuid: z.string(),
  type: z.literal('rule'),
  rule_id: z.number(),
  item_id: z.number(),
  calculation_segment_uuid: z.string(),
  calculation_segment_id: z.number().nullable(),
});

export const calculationSegmentItemSchema = z.union([
  calculationSegmentValueItem,
  calculationSegmentProductItem,
  calculationSegmentRuleItem,
]);

export const calculationSegmentSchema = z.object({
  id: z.number().nullable(),
  uuid: z.string(),
  calculation_id: z.number().nullable(),
  calculation_uuid: z.string(),
  segment_id: z.number(),

  calculation_segment_items: z.array(calculationSegmentItemSchema),
});

export const calculationOwnerSchema = z.object({
  id: z.number(),
  first_name: z.string(),
  last_name: z.string(),
  account_type: z.string().optional(),
});

const calculationChildSchema = z.object({
  id: z.number().nullable(),
  uuid: z.string(),
  client_id: z.number().nullable(),
  client_uuid: z.string(),
  name: z.string(),
  calculator_id: z.number(),
  parent_id: z.number().nullable().optional(),
  owner_user_id: z.number().optional(),
  owner: calculationOwnerSchema.optional(),

  calculation_segments: z.array(calculationSegmentSchema),

  outdated: z.boolean().optional(),

  calculation_created_at: z.string(),
});

export const calculationSchema = calculationChildSchema.extend({
  calculation_children: z.array(calculationChildSchema).optional(),
});

export const calculationsSchema = z.array(calculationSchema);

export const grantLevelOptionSchema = z.object({
  id: z.number(),
  name: z.string(),
  grant_level_id: z.number(),
  value: z.union([z.string(), z.number()]).nullable(),
  slug: z.string(),
});

export const grantLevelSchema = z.object({
  id: z.number(),
  name: z.string(),
  grant_id: z.number(),
  slug: z.string(),
  grant_level_options: z.array(grantLevelOptionSchema),
});

export const grantSchema = z.object({
  id: z.number(),
  name: z.string(),
  grant_levels: z.array(grantLevelSchema),
  slug: z.string(),
});

export const grantDataSchema = z.object({
  grants: z.array(grantSchema),
  id: z.number(),
  slug: z.string(),
  name: z.string(),
});

export const grantDatasSchema = z.array(grantDataSchema);
